import { useGetTagsQuery } from '@carboncopyco/data';
import { PermissionAction, PermissionKey } from '@carboncopyco/types';
import { MultiDropdownFilter, TextBase } from '@carboncopyco/ui';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { usePermissionsContext } from '../../utils';
import { useDashboardProps } from '../DashboardContext';

type ListItem = {
  id: string;
  label: string;
};

export const checkboxClasses = (checked: boolean) =>
  classNames(
    'rounded-sm border outline-none active:outline-none active:ring-0 active:ring-offset-0 focus:outline-none focus:ring-0 focus:ring-offset-0',
    checked
      ? 'hover:text-ds-primary-400 text-ds-primary active:text-ds-primary-500 cursor-pointer'
      : 'cursor-pointer text-ds-primary border-ds-natural-200 hover:border-ds-primary-400 active:border-ds-primary-500'
  );

export const TagsFilter = () => {
  const flags = useFlags();
  const { getPermissions } = usePermissionsContext();
  const { organizationId, selectedTagIds, toggleTagId, clearTagIds } =
    useDashboardProps();
  const tagsPermission = getPermissions(
    PermissionKey.tag,
    PermissionAction.getAllTags
  );
  const shouldGetTagsList = flags.partners && tagsPermission;

  const { data: tags, isLoading } = useGetTagsQuery(
    { organizationId },
    { skip: shouldGetTagsList }
  );

  const selectedItems = {};
  const tagItems = useMemo(
    () =>
      tags?.map(
        (tag): ListItem => ({
          id: tag.id,
          label: tag.name,
        })
      ),
    [tags]
  );

  if (!tagItems || isLoading) {
    return;
  }

  selectedTagIds.forEach((tagId: string) => {
    const tag = tagItems?.find(
      (tagItem) => String(tagItem.id) === String(tagId)
    );
    if (tag) {
      selectedItems[tagId] = tag.label;
    }
  });

  return (
    tags?.length > 0 && (
      <MultiDropdownFilter
        capsuleWidth={200}
        contentWidth={200}
        renderItem={({ checked, onChange, item }) => (
          <div className="px-2 h-9 cursor-pointer flex items-center w-full gap-2 focus:bg-ds-natural-50 focus:outline-none focus:rounded-md">
            <div className="flex gap-3 items-center w-full">
              <input
                checked={checked}
                onChange={onChange}
                type="checkbox"
                className={checkboxClasses(checked)}
              />
              <div className="gap-2 flex items-center grow">
                <div className="truncate max-w-[110px]">
                  <TextBase
                    color="text-ds-natural-600"
                    label={item.label}
                    ellipsis
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        searchableGroup={{ items: tagItems }}
        selectedItems={selectedItems}
        onChange={toggleTagId}
        placeholder="All tags"
        onClear={clearTagIds}
        sideOffset={-10}
      />
    )
  );
};
